import { FC, useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { CustomerForm } from 'app/hooks/useOrderCheckout';
import { FormErrors } from '../validators';
import CheckoutTextField, { OnChangeTextField } from './CheckoutTextField';
import MenuItem from '@mui/material/MenuItem';
import { Text } from '@concepta/react-material-ui';
import countries from '../countries';
import { customerFormFields, FieldTypes } from '../formfields';
import InternationalPhoneInput from 'app/components/InternationalPhoneInput';
import ContactMethodToggle, {
  ContactMethod,
} from 'app/components/ContactMethodToggle';
import { ParsedCountry, CountryIso2 } from 'react-international-phone';
import { getCountryIso2FromDialCode } from 'utils/phoneCountryMapping';

interface Props {
  form: Partial<CustomerForm>;
  errors: FormErrors<CustomerForm>;
  onChange: OnChangeTextField;
  onSelectChange: (event: SelectChangeEvent) => void;
  onPhoneChange?: (name: string, phone: string, country: ParsedCountry) => void;
  onContactMethodChange?: (method: ContactMethod) => void;
  isEdit?: boolean;
}

const Customer: FC<Props> = ({
  form,
  errors,
  onChange,
  onSelectChange,
  onPhoneChange,
  onContactMethodChange,
  isEdit,
}) => {
  const [primaryPhoneCountry, setPrimaryPhoneCountry] = useState<CountryIso2>(
    form?.countryCodeCellPhone
      ? getCountryIso2FromDialCode(form.primaryCellPhone)
      : 'us',
  );
  const [confirmPrimaryPhoneCountry, setConfirmPrimaryPhoneCountry] =
    useState<CountryIso2>(primaryPhoneCountry);
  const [secondaryPhoneCountry, setSecondaryPhoneCountry] =
    useState<CountryIso2>(
      form?.countryCodeSecondCellPhone
        ? getCountryIso2FromDialCode(form.secondaryCellPhone)
        : 'us',
    );
  const [previousPhoneCountry, setPreviousPhoneCountry] =
    useState<CountryIso2>(primaryPhoneCountry);
  const [hasUserSelectedMethod, setHasUserSelectedMethod] =
    useState<boolean>(false);

  useEffect(() => {
    const isIntl = primaryPhoneCountry !== 'us';

    if (
      primaryPhoneCountry !== previousPhoneCountry &&
      onContactMethodChange &&
      !hasUserSelectedMethod
    ) {
      onContactMethodChange(
        isIntl ? ContactMethod.WHATSAPP : ContactMethod.SMS,
      );
    }

    setPreviousPhoneCountry(primaryPhoneCountry);
  }, [
    primaryPhoneCountry,
    onContactMethodChange,
    previousPhoneCountry,
    hasUserSelectedMethod,
  ]);

  const handlePhoneChange = (
    name: string,
    phone: string,
    country: ParsedCountry,
  ) => {
    if (onPhoneChange) {
      onPhoneChange(name, phone, country);
    }

    if (name === 'primaryCellPhone') {
      const countryChanged = country.iso2 !== primaryPhoneCountry;

      setPrimaryPhoneCountry(country.iso2);

      if (countryChanged) {
        setConfirmPrimaryPhoneCountry(country.iso2);
        if (onChange) {
          onChange({ name: 'confirmPrimaryCellPhone', value: '' });
        }
      }

      if (!hasUserSelectedMethod && onContactMethodChange) {
        const isIntl = country.iso2 !== 'us';
        onContactMethodChange(
          isIntl ? ContactMethod.WHATSAPP : ContactMethod.SMS,
        );
      }
    } else if (name === 'confirmPrimaryCellPhone') {
      setConfirmPrimaryPhoneCountry(country.iso2);
    } else if (name === 'secondaryCellPhone') {
      setSecondaryPhoneCountry(country.iso2);
    }
  };

  const handleContactMethodChange = (method: ContactMethod) => {
    setHasUserSelectedMethod(true);
    if (onContactMethodChange) {
      onContactMethodChange(method);
    }
  };
  return (
    <Grid container spacing={3}>
      {customerFormFields(isEdit).map(field => {
        const value = form[field.name as keyof CustomerForm];

        if (
          field.type === FieldTypes.TEXT &&
          (typeof value === 'string' || typeof value === 'undefined')
        ) {
          return (
            <CheckoutTextField
              key={field.name}
              label={field.label}
              name={field.name}
              value={value}
              onChange={onChange}
              required={field.required}
              maxLength={field.maxLength}
              mask={field.mask}
              error={errors[field.name]}
              disabled={field.disabled}
            />
          );
        }

        if (
          field.type === FieldTypes.SELECT &&
          field.name === 'country' &&
          (typeof value === 'string' || typeof value === 'undefined')
        ) {
          return (
            // TODO: change Select component to ROCKET'S Select, but fix variant there first
            <Grid item xs={12} key={field.name}>
              <FormControl
                variant="standard"
                fullWidth
                error={!!errors[field.name]}
              >
                <InputLabel>
                  {field.label}{' '}
                  <Text fontSize={12} display="inline">
                    (required)
                  </Text>
                </InputLabel>
                <Select
                  name={field.name}
                  onChange={onSelectChange}
                  value={value}
                  defaultValue={value}
                >
                  {countries.map(country => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors[field.name] && (
                  <FormHelperText>{errors[field.name]}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          );
        }

        if (
          field.type === FieldTypes.PHONE &&
          (typeof value === 'string' || typeof value === 'undefined')
        ) {
          let defaultCountry: CountryIso2;
          if (field.name === 'primaryCellPhone') {
            defaultCountry = primaryPhoneCountry;
          } else if (field.name === 'confirmPrimaryCellPhone') {
            defaultCountry = confirmPrimaryPhoneCountry;
          } else {
            defaultCountry = secondaryPhoneCountry;
          }

          return (
            <Grid item xs={12} key={field.name}>
              <InternationalPhoneInput
                label={field.label}
                name={field.name}
                value={value || ''}
                onChange={(phone, country) =>
                  handlePhoneChange(field.name, phone, country)
                }
                error={errors[field.name]}
                required={field.required}
                defaultCountry={defaultCountry}
                disableDialCodePrefill={field.name === 'secondaryCellPhone'}
              />
            </Grid>
          );
        }

        if (
          field.type === FieldTypes.CONTACT_METHOD &&
          field.name === 'preferredContactMethod'
        ) {
          return (
            <Grid item xs={12} key={field.name}>
              <ContactMethodToggle
                value={typeof value === 'number' ? value : undefined}
                onChange={handleContactMethodChange}
                error={errors[field.name]}
                required={field.required}
                isInternationalPhone={primaryPhoneCountry !== 'us'}
              />
            </Grid>
          );
        }
        return null;
      })}
    </Grid>
  );
};

export default Customer;
